/**
 * ============================================================================
 * WeightedScoreGauge — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: visual hero for the Vendor Evaluation Scorecard. A circular gauge
 *          with the weighted composite score in the middle, surrounded by
 *          four wedge arcs — one per axis (Quality / Delivery / Audit /
 *          Service). Each wedge length encodes that axis's individual score
 *          (0–100). Inline SVG with the QWise gradient stroke.
 *
 * A11Y: aria-label carries the four axis scores and the composite, so SR
 *  users get a single-shot summary. Visual labels live in a paired legend.
 * ============================================================================ */

import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './VendorEvaluationScorecard.module.css';
import type {
  AxisScore,
  EvaluationAxis,
  EvaluationClassification,
} from './useEvaluationQuery';

const AXIS_ORDER: EvaluationAxis[] = ['QUALITY', 'DELIVERY', 'AUDIT', 'SERVICE'];
const AXIS_COLOR: Record<EvaluationAxis, string> = {
  QUALITY: '#1DD1A1',
  DELIVERY: '#22D3EE',
  AUDIT: '#2563EB',
  SERVICE: '#A855F7',
};

const CLASS_LABEL_CLASS: Record<EvaluationClassification, string> = {
  APPROVED: styles.classApproved,
  CONDITIONAL: styles.classConditional,
  DISQUALIFIED: styles.classDisqualified,
};

export interface WeightedScoreGaugeProps {
  axisScores: AxisScore[];
  weightedScore: number | null;
  classification: EvaluationClassification | null;
}

export function WeightedScoreGauge({
  axisScores,
  weightedScore,
  classification,
}: WeightedScoreGaugeProps) {
  const { t, i18n } = useTranslation();
  const gradId = useId();
  const nf = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language],
  );

  // Index by axis so we can render in canonical order even when the server
  // returns a different order.
  const byAxis = useMemo(() => {
    const m = new Map<EvaluationAxis, AxisScore>();
    axisScores.forEach((a) => m.set(a.axis, a));
    return m;
  }, [axisScores]);

  const composite = weightedScore == null ? 0 : Math.max(0, Math.min(100, weightedScore));
  const r = 64;
  const c = 2 * Math.PI * r;
  const dash = (composite / 100) * c;

  const summaryParts = AXIS_ORDER.map((ax) => {
    const a = byAxis.get(ax);
    const label = t(`supplier.eval.axis.${ax}`, { defaultValue: ax });
    const score = a?.score == null ? '—' : nf.format(a.score);
    return `${label} ${score}`;
  }).join(', ');

  const classLabel = classification
    ? t(`supplier.evaluation.classification.${classification}`, {
        defaultValue: classification,
      })
    : t('supplier.evaluation.classification.PENDING', {
        defaultValue: 'Pending',
      });

  return (
    <section
      className={styles.gaugeWrap}
      aria-label={t('supplier.eval.gaugeAria', {
        defaultValue:
          'Weighted score {{composite}} of 100, classification {{cls}}. Axes: {{summary}}.',
        composite: weightedScore == null ? '—' : nf.format(composite),
        cls: classLabel,
        summary: summaryParts,
      })}
    >
      <svg
        width={180}
        height={180}
        viewBox="0 0 180 180"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1DD1A1" />
            <stop offset="50%" stopColor="#22D3EE" />
            <stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
        </defs>
        {/* Background track */}
        <circle
          cx={90}
          cy={90}
          r={r}
          stroke="var(--color-border)"
          strokeWidth={12}
          fill="none"
        />
        {/* Composite (gradient) */}
        <circle
          cx={90}
          cy={90}
          r={r}
          stroke={`url(#${gradId})`}
          strokeWidth={12}
          fill="none"
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
          transform="rotate(-90 90 90)"
        />
        <text
          x={90}
          y={92}
          textAnchor="middle"
          fontSize={32}
          fontWeight={700}
          fill="var(--color-text)"
        >
          {weightedScore == null ? '—' : nf.format(composite)}
        </text>
        <text
          x={90}
          y={114}
          textAnchor="middle"
          fontSize={11}
          fill="var(--color-text-muted)"
          letterSpacing="0.08em"
        >
          / 100
        </text>
      </svg>

      <ul className={styles.axisLegend}>
        {AXIS_ORDER.map((ax) => {
          const a = byAxis.get(ax);
          const score = a?.score;
          const wpct = a ? Math.round(a.weight * 100) : 0;
          return (
            <li key={ax} className={styles.axisLegendItem}>
              <span
                className={styles.axisLegendSwatch}
                style={{ background: AXIS_COLOR[ax] }}
                aria-hidden="true"
              />
              <span className={styles.axisLegendLabel}>
                {t(`supplier.eval.axis.${ax}`, { defaultValue: ax })}
              </span>
              <span className={styles.axisLegendScore}>
                {score == null ? '—' : nf.format(score)}
              </span>
              <span className={styles.axisLegendWeight}>{wpct}%</span>
            </li>
          );
        })}
      </ul>

      {classification && (
        <div
          className={`${styles.classBadge} ${CLASS_LABEL_CLASS[classification]}`}
          aria-label={t('supplier.eval.classBadgeAria', {
            defaultValue: 'Classification: {{cls}}',
            cls: classLabel,
          })}
        >
          {classLabel}
        </div>
      )}
    </section>
  );
}
